// The `session` wire shapes — the summary row `GET /v1/repos/:o/:r/sessions`
// serves and the detail body `GET /v1/sessions/:id` serves, plus the MCP
// `session` verb's two actions, which serve the same two.
//
// Held together already, by hand-written types in `packages/mcp` that
// `apps/api`'s projections were declared to return. That worked — there is no
// drift here to report — and it is why the types survive as aliases rather than
// being deleted. What a `type` could not do is reach the OpenAPI document, so
// both bodies published as untyped, and a generated client learned nothing
// about a surface that carries money-adjacent fields.
//
// ## The detail body EXTENDS the summary, and that is the contract
//
// `GET /v1/sessions/:id` answers every field of a list row plus repo/entity
// context and the token accounting. Stated as `.extend()` rather than two
// object literals, so a field added to the row reaches the detail body by
// construction — the direction hand-written mirrors get wrong.
//
// ## `reference_usd` is NOT a cost, and the schema says so twice
//
// On a BYOK platform we never charge for tokens, and a large share of prod runs
// are paid for by a flat-rate key whose MARGINAL cost is genuinely $0. This
// field is a public-rate-card comparison, and `reference_basis` names why it is
// absent when it is — so a client renders the RIGHT silence instead of
// collapsing three reasons into a dash. A bare currency figure here would read
// as an invoice.

import { z } from 'zod';
import { ReferenceBasisSchema } from '../value-objects/reference-basis';
import { defineResource } from './resource';

/** The camelCase list row the projection reads. */
export type SessionSummaryEntity = {
  id: string;
  trigger: string;
  entityKind: string;
  outcome: string | null;
  model: string;
  startedAt: Date;
  finishedAt: Date | null;
  /** Triggering issue title; the projection derives `title` from it. */
  issueTitle: string | null;
  prNumber: number | null;
};

/** The detail row — the list row plus repo, entity context and token accounting. */
export type SessionDetailEntity = SessionSummaryEntity & {
  /** Null for a repo-less account session (a gateway parent, plan 101 A15). */
  repoFullName: string | null;
  issueNumber: number | null;
  tokenIn: number;
  tokenOut: number;
  referenceUsd: number | null;
  referenceBasis: z.infer<typeof ReferenceBasisSchema>;
};

/** The list-row body. Field names and nullability are the existing contract. */
export const SessionSummaryWireSchema = z.object({
  id: z.string(),
  /** Already derived: the issue title, else `PR #<n>`, else the raw trigger. */
  title: z.string(),
  trigger: z.string(),
  entity_kind: z.string(),
  /** Null while the session is still running. */
  outcome: z.string().nullable(),
  model: z.string(),
  started_iso: z.string(),
  /** Null while the session is still running. */
  finished_iso: z.string().nullable(),
});

/** What a client should type against for a session list row. */
export type SessionSummaryWire = z.infer<typeof SessionSummaryWireSchema>;

/** The detail body. */
export const SessionDetailWireSchema = SessionSummaryWireSchema.extend({
  /** Null for a repo-less account session (plan 101 A15). */
  repo_full_name: z.string().nullable(),
  /** The issue number, else the PR number, else null. */
  entity_number: z.number().nullable(),
  entity_title: z.string().nullable(),
  token_in: z.number(),
  token_out: z.number(),
  /**
   * What this session's tokens are WORTH at public list rates — NOT a bill and
   * NOT what the run cost. Null unless `reference_basis` is `listed`. Any
   * surface rendering it must label it as a reference.
   */
  reference_usd: z.number().nullable(),
  /** Why `reference_usd` is present or absent. Three silences, told apart. */
  reference_basis: ReferenceBasisSchema,
});

/** What a client should type against for a session detail body. */
export type SessionDetailWire = z.infer<typeof SessionDetailWireSchema>;

/**
 * The derived headline.
 *
 * `||`, not `??`, and that is load-bearing: `issues.title` DEFAULTS TO `''`
 * (#980's pre-cache rows), so a stored empty title must fall through to the PR
 * number rather than render a blank row. `??` would keep the empty string.
 */
function sessionTitle(s: SessionSummaryEntity): string {
  return s.issueTitle || (s.prNumber !== null ? `PR #${s.prNumber}` : s.trigger);
}

function summaryBody(s: SessionSummaryEntity): SessionSummaryWire {
  return {
    id: s.id,
    title: sessionTitle(s),
    trigger: s.trigger,
    entity_kind: s.entityKind,
    outcome: s.outcome,
    model: s.model,
    started_iso: s.startedAt.toISOString(),
    finished_iso: s.finishedAt ? s.finishedAt.toISOString() : null,
  };
}

export const SessionSummaryResource = defineResource<
  SessionSummaryEntity,
  typeof SessionSummaryWireSchema
>({
  schema: SessionSummaryWireSchema,
  serialize: summaryBody,
});

export const SessionDetailResource = defineResource<
  SessionDetailEntity,
  typeof SessionDetailWireSchema
>({
  schema: SessionDetailWireSchema,
  serialize: (s) => ({
    ...summaryBody(s),
    repo_full_name: s.repoFullName,
    entity_number: s.issueNumber ?? s.prNumber,
    // `||` again, same reason as the title: a stored empty issue title reads as
    // absent, not as an empty heading.
    entity_title: s.issueTitle || null,
    token_in: s.tokenIn,
    token_out: s.tokenOut,
    reference_usd: s.referenceUsd,
    reference_basis: s.referenceBasis,
  }),
});
